<template>
  <div class="launcher-update-page ignore-launcher-update">
    <header class="page-header">
      <el-breadcrumb separator="/">
        <el-breadcrumb-item>平台管理</el-breadcrumb-item>
        <el-breadcrumb-item>启动器更新管理</el-breadcrumb-item>
      </el-breadcrumb>
      <el-button size="small" icon="el-icon-refresh" :loading="loading.status" @click="loadStatus(true)">刷新状态</el-button>
    </header>
    <el-alert class="section-alert" type="info" :closable="false" show-icon title="本页配置只影响当前平台（v1 测试平台与正式平台各自独立）" />

    <el-card v-loading="loading.status" shadow="never" class="section-card">
      <div slot="header" class="section-heading">概览</div>
      <div v-if="errors.status" class="error-row">
        <el-alert :title="errors.status" type="error" :closable="false" show-icon />
        <el-button size="small" icon="el-icon-refresh" @click="loadStatus()">重试</el-button>
      </div>
      <div class="overview-grid">
        <div><div class="muted">最新正式版本</div><strong>{{ status ? status.latest_version || '暂无版本' : '-' }}</strong></div>
        <div><div class="muted">线路同步数</div><strong>{{ status ? status.synced_count + ' / ' + status.enabled_count : '-' }}</strong></div>
        <div><div class="muted">上次检查时间（北京时间）</div><strong>{{ formatTime(status && status.checked_at) }}</strong></div>
      </div>
    </el-card>

    <el-card v-loading="loading.status" shadow="never" class="section-card">
      <div slot="header" class="section-heading">各线路当前版本</div>
      <el-alert v-if="errors.status" class="section-alert" :title="errors.status" type="error" :closable="false" show-icon />
      <el-table :data="orderedSources" border stripe>
        <el-table-column label="线路" min-width="230">
          <template slot-scope="scope">
            <strong>{{ sourceName(scope.row.id) }}</strong>
            <div class="cell-note break-text">{{ scope.row.location || '未配置地址' }}</div>
          </template>
        </el-table-column>
        <el-table-column label="Windows 版本" min-width="140">
          <template slot-scope="scope">{{ scope.row.windows ? scope.row.windows.version : (scope.row.reachable ? '未发布' : '-') }}</template>
        </el-table-column>
        <el-table-column label="macOS 版本" min-width="140">
          <template slot-scope="scope">{{ scope.row.mac ? scope.row.mac.version : (scope.row.reachable ? '未发布' : '-') }}</template>
        </el-table-column>
        <el-table-column label="发布时间（北京时间）" min-width="225">
          <template slot-scope="scope">
            <div>Windows：{{ formatTime(scope.row.windows && scope.row.windows.release_date) }}</div>
            <div>macOS：{{ formatTime(scope.row.mac && scope.row.mac.release_date) }}</div>
          </template>
        </el-table-column>
        <el-table-column label="状态" min-width="225">
          <template slot-scope="scope">
            <el-tag size="small" :type="sourceTag(scope.row.state)">{{ sourceLabel(scope.row) }}</el-tag>
            <div v-if="scope.row.error" class="error-text break-text">{{ scope.row.error }}</div>
          </template>
        </el-table-column>
      </el-table>
      <el-alert v-if="hasBehindSource" class="section-alert after-table" type="warning" :closable="false" show-icon title="请在 Builder 执行 mirror-update-feed 同步该版本" />
    </el-card>

    <el-card v-loading="loading.status" shadow="never" class="section-card">
      <div slot="header" class="section-heading">当前版本更新说明</div>
      <el-alert v-if="errors.status" :title="errors.status" type="error" :closable="false" />
      <div v-else class="plain-text">{{ status && status.release_notes || '暂无更新说明' }}</div>
    </el-card>

    <el-card v-loading="loading.releases" shadow="never" class="section-card">
      <div slot="header" class="section-heading">
        <span>历史版本</span>
        <el-button type="text" icon="el-icon-refresh" :disabled="loading.releases" @click="loadReleases()">刷新</el-button>
      </div>
      <el-alert v-if="errors.releases" class="section-alert" :title="errors.releases" type="error" :closable="false" show-icon />
      <el-alert v-else-if="!releases.reachable" class="section-alert" title="平台无法访问 GitHub" :description="releases.error || '未返回具体原因'" type="error" :closable="false" show-icon />
      <el-collapse v-if="releases.items.length">
        <el-collapse-item v-for="release in releases.items" :key="release.version" :name="release.version">
          <template slot="title"><span class="release-title">{{ release.version }}</span><span class="muted">{{ formatTime(release.published_at) }}</span></template>
          <div class="plain-text">{{ release.notes || '暂无更新说明' }}</div>
        </el-collapse-item>
      </el-collapse>
      <el-empty v-else-if="!loading.releases && !errors.releases && releases.reachable" description="暂无历史版本" :image-size="60" />
    </el-card>

    <el-card shadow="never" class="section-card">
      <div slot="header" class="section-heading"><span>设备版本</span><el-button type="text" icon="el-icon-refresh" @click="refreshDevices()">刷新</el-button></div>
      <div v-loading="loading.summary" class="summary-area">
        <div v-if="errors.summary" class="error-row">
          <el-alert :title="errors.summary" type="error" :closable="false" show-icon />
          <el-button size="small" icon="el-icon-refresh" @click="loadSummary()">重试</el-button>
        </div>
        <template v-if="summary">
          <el-alert v-if="status && !status.latest_version" type="info" :closable="false" show-icon title="平台暂时取不到 GitHub 最新版本，设备是否需要更新无法判断，「需要更新」筛选为空。" style="margin-bottom: 10px" />
          <div class="summary-line">最近 {{ summary.active_days }} 天活跃设备：{{ summary.total }} 台；安装失败：{{ summary.install_failed }} 台<span class="muted">统计时间：{{ formatTime(summary.generated_at) }}</span></div>
          <div v-for="item in summary.versions" :key="item.version" class="version-bar">
            <span class="version-name">{{ item.version }}</span>
            <el-progress :percentage="versionPercent(item.count)" :color="versionColor(item.version)" />
            <span class="version-count">{{ item.count }} 台</span>
          </div>
          <el-empty v-if="!summary.versions.length" description="暂无活跃设备版本数据" :image-size="60" />
        </template>
      </div>
      <el-divider />
      <el-form :inline="true" size="small" class="filter-form" @submit.native.prevent="searchDevices">
        <el-form-item label="状态">
          <el-select v-model="deviceFilters.filter" @change="searchDevices">
            <el-option label="全部" value="all" /><el-option label="需要更新" value="behind" /><el-option label="安装失败" value="install_failed" />
          </el-select>
        </el-form-item>
        <el-form-item label="版本">
          <el-select v-model="deviceFilters.version" filterable clearable placeholder="全部版本" @change="searchDevices">
            <el-option v-for="version in deviceVersionOptions" :key="version" :label="version" :value="version" />
          </el-select>
        </el-form-item>
        <el-form-item label="设备码">
          <el-input v-model.trim="deviceFilters.keyword" maxlength="64" clearable placeholder="搜索设备码" @clear="searchDevices" />
        </el-form-item>
        <el-form-item><el-button type="primary" icon="el-icon-search" :loading="loading.devices" @click="searchDevices">查询</el-button></el-form-item>
      </el-form>
      <el-alert v-if="errors.devices" class="section-alert" :title="errors.devices" type="error" :closable="false" show-icon />
      <el-table v-loading="loading.devices" :data="devices.items" border stripe>
        <el-table-column prop="device_code_masked" label="设备码" min-width="170" show-overflow-tooltip />
        <el-table-column label="启动器版本" min-width="140"><template slot-scope="scope">{{ scope.row.launcher_version || '未上报' }}</template></el-table-column>
        <el-table-column label="系统 / 架构" min-width="135"><template slot-scope="scope">{{ scope.row.os || '-' }} / {{ scope.row.arch || '-' }}</template></el-table-column>
        <el-table-column label="上报时间（北京时间）" min-width="180"><template slot-scope="scope">{{ formatTime(scope.row.reported_at) }}</template></el-table-column>
        <el-table-column label="状态" min-width="185"><template slot-scope="scope"><el-tag size="small" :type="deviceTag(scope.row.state)">{{ deviceLabel(scope.row) }}</el-tag></template></el-table-column>
      </el-table>
      <el-pagination class="pagination" background layout="total, prev, pager, next" :current-page="devicePage" :page-size="20" :total="devices.total" @current-change="changeDevicePage" />
    </el-card>

    <el-card v-loading="loading.config" shadow="never" class="section-card">
      <div slot="header" class="section-heading">更新策略</div>
      <div v-if="errors.config" class="error-row">
        <el-alert :title="errors.config" type="error" :closable="false" show-icon />
        <el-button size="small" icon="el-icon-refresh" :disabled="configBusy" @click="refreshConfig()">刷新配置</el-button>
      </div>
      <el-form :model="form.policy" label-width="180px" size="small" :disabled="configDisabled" class="config-form">
        <el-form-item label="更新方式">
          <el-radio-group v-model="form.policy.mode" size="small"><el-radio-button label="normal">普通</el-radio-button><el-radio-button label="force">强制</el-radio-button></el-radio-group>
        </el-form-item>
        <el-form-item label="最低支持版本" :error="configFieldErrors.min_version">
          <el-select v-model="form.policy.min_version" filterable clearable placeholder="未设置" class="full-width">
            <el-option v-for="version in policyVersionOptions" :key="version" :value="version" :label="version + (syncedVersions.includes(version) ? '（已同步）' : '')" />
          </el-select>
          <div class="form-note">强制策略的最低版本必须在所有已启用线路同步，保存时由平台校验。</div>
        </el-form-item>
        <el-form-item label="生效时间" :error="configFieldErrors.effective_at">
          <el-select v-model="effectiveChoice" class="full-width" @change="changeEffectiveChoice">
            <el-option label="立即" value="immediate" /><el-option label="1 天后" value="1" /><el-option label="3 天后" value="3" /><el-option label="7 天后" value="7" /><el-option label="自定义日期时间" value="custom" />
          </el-select>
          <el-date-picker v-if="effectiveChoice === 'custom'" v-model="effectiveInput" class="full-width date-input" type="datetime" format="yyyy-MM-dd HH:mm" value-format="yyyy-MM-dd HH:mm" placeholder="北京时间" @change="changeCustomEffective" />
          <div v-else-if="effectiveChoice !== 'immediate'" class="form-note">北京时间：{{ formatTime(form.policy.effective_at) }}</div>
        </el-form-item>
        <el-form-item label="禁用版本" :error="configFieldErrors.blocked_versions">
          <el-select v-model="form.policy.blocked_versions" multiple filterable allow-create default-first-option :multiple-limit="20" class="full-width" placeholder="选择或输入版本">
            <el-option v-for="version in policyVersionOptions" :key="version" :label="version" :value="version" />
          </el-select>
        </el-form-item>
        <el-form-item label="提示文案" :error="configFieldErrors.message"><el-input v-model="form.policy.message" type="textarea" :rows="3" maxlength="200" show-word-limit /></el-form-item>
        <el-form-item label="拦截未上报版本的旧启动器">
          <el-switch v-model="form.policy.block_unreported" />
          <span class="switch-note">会影响 {{ impact ? impact.unreported : '待预估' }} 台设备</span>
        </el-form-item>
      </el-form>
      <div v-loading="loading.impact" class="impact-area">
        <div v-if="errors.impact" class="error-row">
          <el-alert :title="errors.impact" type="error" :closable="false" show-icon />
          <el-button size="small" icon="el-icon-refresh" :disabled="!configReady || loading.impact" @click="loadImpact()">重试预估</el-button>
        </div>
        <el-alert v-else-if="impact" :title="impactText" :type="form.policy.mode === 'force' ? 'warning' : 'info'" :closable="false" show-icon />
        <div v-else class="muted">影响预估待加载</div>
      </div>
    </el-card>

    <el-card v-loading="loading.config" shadow="never" class="section-card">
      <div slot="header" class="section-heading"><span>线路配置</span><el-button type="text" icon="el-icon-time" @click="openAudits()">查看修改记录</el-button></div>
      <el-alert v-if="!configReady && errors.config" class="section-alert" :title="errors.config" type="error" :closable="false" show-icon />
      <el-form label-width="110px" size="small" :disabled="configDisabled" class="source-form">
        <div class="source-fields">
          <el-form-item label="GitHub"><el-switch v-model="form.sources.github.enabled" active-text="启用" /></el-form-item>
          <el-form-item label="Owner" :error="configFieldErrors.owner"><el-input v-model.trim="form.sources.github.owner" maxlength="39" clearable /></el-form-item>
          <el-form-item label="Repo" :error="configFieldErrors.repo"><el-input v-model.trim="form.sources.github.repo" maxlength="100" clearable /></el-form-item>
        </div>
        <div class="source-fields">
          <el-form-item label="R2"><el-switch v-model="form.sources.r2.enabled" active-text="启用" /></el-form-item>
          <el-form-item label="地址" :error="configFieldErrors.r2"><el-input v-model.trim="form.sources.r2.url" maxlength="512" clearable placeholder="https://" /></el-form-item>
        </div>
        <div class="source-fields">
          <el-form-item label="兜底服务器"><el-switch v-model="form.sources.fallback.enabled" active-text="启用" /></el-form-item>
          <el-form-item label="地址" :error="configFieldErrors.fallback"><el-input v-model.trim="form.sources.fallback.url" maxlength="512" clearable placeholder="https://" /></el-form-item>
        </div>
        <div class="source-fields">
          <el-form-item label="手动下载页" :error="configFieldErrors.download_page_url">
            <el-input v-model.trim="form.sources.download_page_url" maxlength="512" clearable placeholder="未设置" />
          </el-form-item>
        </div>
      </el-form>
      <el-alert class="section-alert" title="不在白名单内的地址无法保存，启动器也会忽略" :description="'allowed_hosts：' + (configMeta.allowed_hosts.join('、') || '暂无白名单')" type="info" :closable="false" show-icon />
      <div class="config-footer">
        <div class="muted">修订号：{{ configReady ? configMeta.revision : '-' }}<br>最后修改：{{ configMeta.updated_by || '-' }} · {{ formatTime(configMeta.updated_at) }}（北京时间）</div>
        <div class="footer-actions">
          <el-button size="small" icon="el-icon-refresh" :disabled="configBusy || confirmVisible" @click="refreshConfig()">刷新配置</el-button>
          <el-button size="small" type="primary" icon="el-icon-check" :loading="configBusy" :disabled="!configReady || revisionConflict || confirmVisible" @click="prepareSave()">保存线路与策略</el-button>
        </div>
      </div>
    </el-card>

    <el-card shadow="never" class="section-card">
      <div slot="header" class="section-heading">
        <span>更新公告</span>
        <div><el-button type="text" icon="el-icon-refresh" :disabled="loading.announcements" @click="loadAnnouncements()">刷新</el-button><el-button size="small" type="primary" icon="el-icon-plus" @click="openAnnouncement()">新建公告</el-button></div>
      </div>
      <el-alert v-if="errors.announcements" class="section-alert" :title="errors.announcements" type="error" :closable="false" show-icon />
      <el-table v-loading="loading.announcements" :data="announcements.items" border stripe>
        <el-table-column prop="title" label="标题" min-width="210" show-overflow-tooltip />
        <el-table-column label="类型" min-width="135"><template slot-scope="scope">{{ kindLabel(scope.row.kind) }}</template></el-table-column>
        <el-table-column label="展示时间（北京时间）" min-width="200"><template slot-scope="scope"><div>{{ formatTime(scope.row.starts_at) }}</div><div>至 {{ formatTime(scope.row.ends_at) }}</div></template></el-table-column>
        <el-table-column label="状态" width="100"><template slot-scope="scope"><el-tag size="small" :type="announcementTag(scope.row.status)">{{ announcementStatus(scope.row.status) }}</el-tag></template></el-table-column>
        <el-table-column label="操作" width="100" fixed="right">
          <template slot-scope="scope">
            <el-tooltip content="编辑公告" placement="top"><el-button type="text" icon="el-icon-edit" aria-label="编辑公告" :disabled="announcementSaving || deletingId !== null" @click="openAnnouncement(scope.row)" /></el-tooltip>
            <el-tooltip content="删除公告" placement="top"><el-button type="text" class="danger-action" icon="el-icon-delete" aria-label="删除公告" :loading="deletingId === scope.row.id" :disabled="deletingId !== null || announcementSaving" @click="removeAnnouncement(scope.row)" /></el-tooltip>
          </template>
        </el-table-column>
      </el-table>
      <el-pagination class="pagination" background layout="total, prev, pager, next" :current-page="announcementPage" :page-size="20" :total="announcements.total" @current-change="changeAnnouncementPage" />
    </el-card>

    <el-dialog title="确认强制更新变更" :visible.sync="confirmVisible" width="560px" :close-on-click-modal="false" :close-on-press-escape="!saving" :show-close="!saving" @closed="clearConfirmation">
      <el-alert class="section-alert" :title="confirmReasons.join('；')" type="warning" :closable="false" show-icon />
      <div v-loading="loading.impact" class="confirmation-impact">
        <template v-if="impact"><p>最近 7 天活跃设备：{{ impact.active_total }} 台</p><p>受影响：<strong>{{ impact.affected }} 台</strong>，其中安装失败 {{ impact.affected_install_failed }} 台</p><p>未上报版本：{{ impact.unreported }} 台</p></template>
        <div v-if="errors.impact" class="error-row"><el-alert :title="errors.impact" type="error" :closable="false" /><el-button size="small" icon="el-icon-refresh" @click="loadImpact(pendingPayload.policy)">重试</el-button></div>
      </div>
      <p>请输入“强制更新”以确认提交。</p>
      <el-input v-model="confirmText" :disabled="saving" maxlength="16" placeholder="强制更新" @keyup.enter.native="confirmSave" />
      <el-alert v-if="errors.config" class="after-table" :title="errors.config" type="error" :closable="false" />
      <span slot="footer">
        <el-button :disabled="saving" @click="confirmVisible = false">取消</el-button>
        <el-button type="danger" icon="el-icon-check" :loading="saving" :disabled="confirmText !== '强制更新' || !impact || loading.impact || !!errors.impact || revisionConflict" @click="confirmSave">确认保存</el-button>
      </span>
    </el-dialog>

    <el-dialog title="修改记录" :visible.sync="auditsVisible" width="980px" top="5vh">
      <el-alert v-if="errors.audits" class="section-alert" :title="errors.audits" type="error" :closable="false" show-icon />
      <el-button v-if="errors.audits" size="small" icon="el-icon-refresh" @click="loadAudits()">重试</el-button>
      <el-table v-loading="loading.audits" :data="audits.items" border stripe max-height="560">
        <el-table-column type="expand">
          <template slot-scope="scope"><div class="audit-snapshots"><div><strong>修改前</strong><pre>{{ formatJson(scope.row.before) }}</pre></div><div><strong>修改后</strong><pre>{{ formatJson(scope.row.after) }}</pre></div></div></template>
        </el-table-column>
        <el-table-column label="目标" min-width="140"><template slot-scope="scope">{{ scope.row.target === 'config' ? '配置' : '公告' }}{{ scope.row.target_id ? ' #' + scope.row.target_id : '' }}</template></el-table-column>
        <el-table-column label="操作" width="90"><template slot-scope="scope">{{ auditAction(scope.row.action) }}</template></el-table-column>
        <el-table-column label="修订号" width="95"><template slot-scope="scope">{{ scope.row.revision == null ? '-' : scope.row.revision }}</template></el-table-column>
        <el-table-column prop="operator" label="修改人" min-width="140" />
        <el-table-column label="时间（北京时间）" min-width="180"><template slot-scope="scope">{{ formatTime(scope.row.created_at) }}</template></el-table-column>
      </el-table>
      <el-pagination class="pagination" background layout="total, prev, pager, next" :current-page="auditPage" :page-size="20" :total="audits.total" @current-change="changeAuditPage" />
      <span slot="footer"><el-button @click="auditsVisible = false">关闭</el-button></span>
    </el-dialog>

    <el-dialog :title="announcementForm.id ? '编辑公告' : '新建公告'" :visible.sync="announcementVisible" width="1040px" top="5vh" :close-on-click-modal="false" :close-on-press-escape="!announcementSaving" :show-close="!announcementSaving">
      <el-alert v-if="announcementError" class="section-alert" :title="announcementError" type="error" :closable="false" show-icon />
      <div class="announcement-editor">
        <el-form label-width="115px" size="small" :disabled="announcementSaving">
          <el-form-item label="类型"><el-select v-model="announcementForm.kind" class="full-width"><el-option label="强制更新通知" value="force" /><el-option label="版本预告" value="preview" /><el-option label="普通通知" value="info" /></el-select></el-form-item>
          <el-form-item label="标题" :error="announcementFieldErrors.title"><el-input v-model="announcementForm.title" maxlength="40" show-word-limit /></el-form-item>
          <el-form-item label="内容" :error="announcementFieldErrors.content"><el-input v-model="announcementForm.content" type="textarea" :rows="5" maxlength="500" show-word-limit /></el-form-item>
          <el-form-item label="展示对象"><el-radio-group v-model="announcementTarget"><el-radio label="all">全部</el-radio><el-radio label="below">低于某版本</el-radio></el-radio-group></el-form-item>
          <el-form-item v-if="announcementTarget === 'below'" label="版本" :error="announcementFieldErrors.target_below_version"><el-select v-model="announcementForm.target_below_version" filterable allow-create default-first-option class="full-width" placeholder="选择或输入版本"><el-option v-for="version in policyVersionOptions" :key="version" :label="version" :value="version" /></el-select></el-form-item>
          <el-form-item label="开始时间" :error="announcementFieldErrors.starts_at"><el-date-picker v-model="announcementForm.starts_at" class="full-width" type="datetime" format="yyyy-MM-dd HH:mm" value-format="yyyy-MM-dd HH:mm" placeholder="北京时间" /></el-form-item>
          <el-form-item label="结束时间" :error="announcementFieldErrors.ends_at"><el-date-picker v-model="announcementForm.ends_at" class="full-width" type="datetime" format="yyyy-MM-dd HH:mm" value-format="yyyy-MM-dd HH:mm" placeholder="北京时间" /></el-form-item>
          <el-form-item label="展示方式" :error="announcementFieldErrors.display"><el-checkbox-group v-model="announcementForm.display"><el-checkbox label="banner">首页横条</el-checkbox><el-checkbox label="popup">启动时弹窗</el-checkbox></el-checkbox-group></el-form-item>
        </el-form>
        <div class="announcement-preview">
          <h4>启动器效果预览</h4>
          <div v-if="announcementForm.display.includes('banner')" class="preview-banner"><i class="el-icon-bell" /><div><strong>{{ announcementForm.title || '公告标题' }}</strong><div class="plain-text">{{ announcementForm.content || '公告内容' }}</div></div></div>
          <div v-if="announcementForm.display.includes('popup')" class="preview-window"><div class="preview-window-title">启动器更新公告</div><div class="preview-window-body"><h4>{{ announcementForm.title || '公告标题' }}</h4><div class="plain-text">{{ announcementForm.content || '公告内容' }}</div></div><div class="preview-window-footer"><el-button size="small" type="primary" disabled>我知道了</el-button></div></div>
        </div>
      </div>
      <span slot="footer"><el-button :disabled="announcementSaving" @click="announcementVisible = false">取消</el-button><el-button type="primary" icon="el-icon-check" :loading="announcementSaving" @click="saveAnnouncement()">保存公告</el-button></span>
    </el-dialog>
  </div>
</template>

<script>
import {
  getLauncherUpdateConfig, saveLauncherUpdateConfig, getLauncherUpdateStatus,
  listLauncherUpdateReleases, getLauncherDeviceSummary, listLauncherDevices,
  getLauncherUpdateImpact, listLauncherUpdateAudits, listLauncherAnnouncements,
  createLauncherAnnouncement, updateLauncherAnnouncement, deleteLauncherAnnouncement,
  launcherUpdateErrorMessage
} from '@/api/launcherUpdate'

const SOURCE_IDS = ['github', 'r2', 'fallback']
const HOUR = 60 * 60 * 1000

function clone(value) { return JSON.parse(JSON.stringify(value)) }
function nullable(value) { return String(value || '').trim() || null }
function textLength(value) { return Array.from(String(value || '')).length }

function emptyConfig() {
  return {
    sources: {
      github: { enabled: false, owner: null, repo: null },
      r2: { enabled: false, url: null },
      fallback: { enabled: false, url: null },
      download_page_url: null
    },
    policy: {
      mode: 'normal', min_version: null, effective_at: null, blocked_versions: [],
      message: '', block_unreported: false, running_grace_seconds: 300
    }
  }
}

function emptyAnnouncement() {
  return { id: null, kind: 'info', title: '', content: '', target_below_version: null, starts_at: '', ends_at: '', display: ['banner', 'popup'] }
}

// 使用 UTC 字段换算固定的北京时间，不依赖浏览器所在时区。
function beijingTime(value) {
  if (!value) return ''
  const date = new Date(Date.parse(value) + 8 * HOUR)
  if (!Number.isFinite(date.getTime())) return ''
  const pad = number => String(number).padStart(2, '0')
  return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())} ${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}`
}

function beijingIso(value) {
  if (!/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}$/.test(String(value || ''))) return null
  const iso = value.replace(' ', 'T') + ':00+08:00'
  return beijingTime(iso) === value ? iso : null
}

function parseVersion(value) {
  if (typeof value !== 'string' || value.length > 64) return null
  const match = /^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)(?:-([0-9A-Za-z-]+(?:\.[0-9A-Za-z-]+)*))?$/.exec(value)
  if (!match) return null
  const pre = match[4] ? match[4].split('.') : []
  if (pre.some(part => /^\d+$/.test(part) && part.length > 1 && part[0] === '0')) return null
  return { core: match.slice(1, 4), pre }
}

function numericCompare(a, b) {
  return a.length === b.length ? (a === b ? 0 : a > b ? 1 : -1) : a.length > b.length ? 1 : -1
}

function compareVersions(a, b) {
  const left = parseVersion(a)
  const right = parseVersion(b)
  if (!left || !right) return null
  for (let i = 0; i < 3; i += 1) {
    const compared = numericCompare(left.core[i], right.core[i])
    if (compared) return compared
  }
  if (!left.pre.length || !right.pre.length) return left.pre.length === right.pre.length ? 0 : left.pre.length ? -1 : 1
  for (let i = 0; i < Math.max(left.pre.length, right.pre.length); i += 1) {
    if (left.pre[i] === undefined) return -1
    if (right.pre[i] === undefined) return 1
    const l = left.pre[i]
    const r = right.pre[i]
    if (l === r) continue
    const ln = /^\d+$/.test(l)
    const rn = /^\d+$/.test(r)
    if (ln && rn) return numericCompare(l, r)
    if (ln !== rn) return ln ? -1 : 1
    return l > r ? 1 : -1
  }
  return 0
}

function sortVersions(values) {
  return [...new Set(values.filter(value => parseVersion(value)))].sort((a, b) => compareVersions(b, a))
}

function validHttps(value) {
  if (!value) return true
  try {
    const url = new URL(value)
    return value.length <= 512 && value.startsWith('https://') && url.protocol === 'https:' && !!url.hostname && !url.username && !url.password
  } catch (error) { return false }
}

function confirmationReasons(before, after) {
  const reasons = []
  if (before.mode !== 'force' && after.mode === 'force') reasons.push('新启用强制更新')
  if (after.min_version && (!before.min_version || compareVersions(after.min_version, before.min_version) > 0)) reasons.push('提高最低支持版本')
  if (after.blocked_versions.some(version => !before.blocked_versions.includes(version))) reasons.push('新增禁用版本')
  if (!before.block_unreported && after.block_unreported) reasons.push('开启未上报版本拦截')
  if (before.effective_at && Date.parse(after.effective_at || new Date().toISOString()) < Date.parse(before.effective_at)) reasons.push('提前生效时间')
  return reasons
}

function impactKey(policy) {
  return JSON.stringify([nullable(policy.min_version), [...new Set(policy.blocked_versions.map(nullable).filter(Boolean))], policy.block_unreported])
}

export default {
  name: 'LauncherUpdateManage',
  data() {
    return {
      loading: { status: false, releases: false, config: false, summary: false, devices: false, impact: false, audits: false, announcements: false },
      errors: { status: '', releases: '', config: '', summary: '', devices: '', impact: '', audits: '', announcements: '' },
      status: null,
      releases: { reachable: true, error: null, items: [] },
      summary: null,
      devices: { total: 0, items: [] },
      deviceFilters: { filter: 'all', version: '', keyword: '' },
      devicePage: 1,
      form: emptyConfig(),
      originalConfig: null,
      configReady: false,
      configMeta: { revision: 0, allowed_hosts: [], updated_by: null, updated_at: null },
      configFieldErrors: {},
      revisionConflict: false,
      effectiveChoice: 'immediate',
      effectiveInput: '',
      effectiveEdited: false,
      saving: false,
      impact: null,
      impactTimer: null,
      impactSequence: 0,
      devicesSequence: 0,
      auditsSequence: 0,
      announcementsSequence: 0,
      destroyed: false,
      confirmVisible: false,
      confirmText: '',
      confirmReasons: [],
      pendingPayload: null,
      auditsVisible: false,
      auditPage: 1,
      audits: { total: 0, items: [] },
      announcementPage: 1,
      announcements: { total: 0, items: [] },
      announcementVisible: false,
      announcementSaving: false,
      announcementError: '',
      announcementFieldErrors: {},
      announcementForm: emptyAnnouncement(),
      announcementOriginalTimes: { starts_at: null, ends_at: null },
      announcementTarget: 'all',
      deletingId: null
    }
  },
  computed: {
    configBusy() { return this.loading.config || this.saving },
    configDisabled() { return !this.configReady || this.configBusy || this.confirmVisible || this.revisionConflict },
    hasConfigChanges() {
      return this.originalConfig && (JSON.stringify(this.form) !== JSON.stringify(this.originalConfig) || this.effectiveEdited)
    },
    orderedSources() {
      if (!this.status) return []
      return SOURCE_IDS.map(id => this.status.sources.find(source => source.id === id)).filter(Boolean)
    },
    hasBehindSource() { return this.orderedSources.some(source => source.state === 'behind') },
    syncedVersions() {
      const enabled = this.orderedSources.filter(source => source.enabled)
      if (!enabled.length || SOURCE_IDS.some(id => this.originalConfig && JSON.stringify(this.form.sources[id]) !== JSON.stringify(this.originalConfig.sources[id]))) return []
      if (enabled.some(source => !source.reachable || !['ok', 'consistent', 'partial'].includes(source.state) || (!source.windows && !source.mac))) return []
      const versions = enabled.reduce((values, source) => values.concat(source.windows && source.windows.version, source.mac && source.mac.version), []).filter(Boolean)
      return versions.every(version => version === versions[0]) ? sortVersions(versions) : []
    },
    policyVersionOptions() {
      const reported = this.orderedSources.filter(source => source.enabled && source.reachable && ['ok', 'consistent', 'partial'].includes(source.state))
        .reduce((values, source) => values.concat(source.windows && source.windows.version, source.mac && source.mac.version), [])
      return sortVersions(reported.concat(this.releases.items.map(item => item.version), this.form.policy.min_version))
    },
    deviceVersionOptions() {
      return sortVersions(this.policyVersionOptions.concat(this.summary ? this.summary.versions.map(item => item.version) : []))
    },
    impactText() {
      if (!this.impact) return ''
      const prefix = this.form.policy.mode === 'normal' ? '普通更新不拦截；按强制策略条件预估：' : '影响预估：'
      return `${prefix}${this.impact.affected} / ${this.impact.active_total} 台活跃设备，其中安装失败 ${this.impact.affected_install_failed} 台；未上报版本 ${this.impact.unreported} 台`
    }
  },
  watch: {
    'form.policy': { deep: true, handler() { if (this.configReady) this.scheduleImpact() } }
  },
  mounted() {
    this.loadStatus()
    this.loadReleases()
    this.loadConfig()
    this.loadSummary()
    this.loadDevices()
    this.loadAnnouncements()
  },
  beforeDestroy() {
    this.destroyed = true
    clearTimeout(this.impactTimer)
    this.impactSequence += 1
    this.devicesSequence += 1
    this.auditsSequence += 1
    this.announcementsSequence += 1
  },
  methods: {
    formatTime(value) { return beijingTime(value) || '-' },
    formatJson(value) { return value == null ? '-' : JSON.stringify(value, null, 2) },
    sourceName(id) { return { github: 'GitHub', r2: 'R2', fallback: '兜底服务器' }[id] || id },
    sourceTag(state) { return { ok: 'success', consistent: 'success', partial: 'warning', behind: 'warning', ahead: '', mismatch: 'danger', unreachable: 'danger', disabled: 'info', not_configured: 'info' }[state] || '' },
    sourceLabel(source) {
      if (source.state === 'behind') return source.behind_versions == null ? '落后（版本数未知）' : `落后 ${source.behind_versions} 个版本`
      if (source.state === 'partial') return !source.windows ? '仅 macOS' : !source.mac ? '仅 Windows' : '仅单平台可比较'
      return { ok: '正常', consistent: '与 GitHub 一致', ahead: '领先 GitHub', mismatch: '校验值不一致', unreachable: '无法访问', disabled: '已禁用', not_configured: '未配置' }[source.state] || '未知状态'
    },
    deviceTag(state) { return { latest: 'success', behind: 'warning', install_failed: 'danger', unreported: 'info' }[state] || 'info' },
    deviceLabel(row) {
      if (row.state === 'behind') return row.behind_versions == null ? '落后（版本数未知）' : `落后 ${row.behind_versions} 个版本`
      if (row.state === 'install_failed') return `安装失败 ${row.install_failures} 次`
      return { latest: '已是最新', unreported: '未上报版本', unknown: '无法判断（平台取不到 GitHub 最新版本）' }[row.state] || '未知状态'
    },
    kindLabel(kind) { return { force: '强制更新通知', preview: '版本预告', info: '普通通知' }[kind] || '未知类型' },
    announcementStatus(state) { return { scheduled: '未开始', active: '展示中', ended: '已结束' }[state] || '未知状态' },
    announcementTag(state) { return { scheduled: 'warning', active: 'success', ended: 'info' }[state] || 'info' },
    auditAction(action) { return { save: '保存', create: '新建', update: '修改', delete: '删除' }[action] || '未知操作' },
    versionPercent(count) { return this.summary && this.summary.total ? Math.min(100, Math.round(count / this.summary.total * 1000) / 10) : 0 },
    versionColor(version) {
      if (this.status && version === this.status.latest_version) return '#67c23a'
      if (this.form.policy.blocked_versions.includes(version) || (this.form.policy.mode === 'force' && compareVersions(version, this.form.policy.min_version) < 0)) return '#f56c6c'
      return '#e6a23c'
    },
    async loadStatus(refresh = false) {
      if (this.loading.status) return
      this.loading.status = true
      this.errors.status = ''
      try { this.status = await getLauncherUpdateStatus(refresh) }
      catch (error) { this.errors.status = launcherUpdateErrorMessage(error, '读取线路状态失败') }
      finally { this.loading.status = false }
    },
    async loadReleases() {
      if (this.loading.releases) return
      this.loading.releases = true
      this.errors.releases = ''
      try { this.releases = await listLauncherUpdateReleases() }
      catch (error) { this.errors.releases = launcherUpdateErrorMessage(error, '读取历史版本失败') }
      finally { this.loading.releases = false }
    },
    applyConfig(config) {
      this.form = { sources: clone(config.sources), policy: clone(config.policy) }
      this.originalConfig = clone(this.form)
      this.configMeta = { revision: config.revision, allowed_hosts: config.allowed_hosts || [], updated_by: config.updated_by, updated_at: config.updated_at }
      this.effectiveChoice = config.policy.effective_at && Date.parse(config.policy.effective_at) > Date.now() ? 'custom' : 'immediate'
      this.effectiveInput = beijingTime(config.policy.effective_at)
      this.effectiveEdited = false
      this.configFieldErrors = {}
      this.revisionConflict = false
      this.configReady = true
      this.scheduleImpact()
    },
    async loadConfig() {
      if (this.loading.config) return
      this.loading.config = true
      this.errors.config = ''
      try { this.applyConfig(await getLauncherUpdateConfig()) }
      catch (error) { this.errors.config = launcherUpdateErrorMessage(error, '读取配置失败') }
      finally { this.loading.config = false }
    },
    async refreshConfig() {
      if (this.configBusy || this.confirmVisible) return
      if (this.hasConfigChanges) {
        try { await this.$confirm('刷新将放弃本页尚未保存的线路和策略修改，是否继续？', '刷新配置', { type: 'warning', confirmButtonText: '刷新', cancelButtonText: '取消' }) }
        catch (error) { return }
      }
      await this.loadConfig()
    },
    async loadSummary() {
      if (this.loading.summary) return
      this.loading.summary = true
      this.errors.summary = ''
      try { this.summary = await getLauncherDeviceSummary() }
      catch (error) { this.errors.summary = launcherUpdateErrorMessage(error, '读取设备版本分布失败') }
      finally { this.loading.summary = false }
    },
    refreshDevices() { this.loadSummary(); this.loadDevices() },
    searchDevices() { this.devicePage = 1; this.loadDevices() },
    changeDevicePage(page) { this.devicePage = page; this.loadDevices() },
    async loadDevices() {
      const sequence = ++this.devicesSequence
      this.loading.devices = true
      this.errors.devices = ''
      try {
        const data = await listLauncherDevices({
          filter: this.deviceFilters.filter, version: this.deviceFilters.version || undefined,
          keyword: this.deviceFilters.keyword || undefined, page: this.devicePage, size: 20
        })
        if (sequence === this.devicesSequence) this.devices = data
      } catch (error) {
        if (sequence === this.devicesSequence) this.errors.devices = launcherUpdateErrorMessage(error, '读取设备列表失败')
      } finally { if (sequence === this.devicesSequence) this.loading.devices = false }
    },
    changeEffectiveChoice(choice) {
      this.effectiveEdited = true
      if (choice === 'custom') {
        this.effectiveInput = beijingTime(this.form.policy.effective_at)
        this.form.policy.effective_at = beijingIso(this.effectiveInput)
      } else {
        this.form.policy.effective_at = new Date(Date.now() + (choice === 'immediate' ? 0 : Number(choice) * 24 * HOUR)).toISOString()
      }
    },
    changeCustomEffective(value) {
      this.effectiveEdited = true
      this.form.policy.effective_at = beijingIso(value)
    },
    scheduleImpact() {
      clearTimeout(this.impactTimer)
      this.impactSequence += 1
      this.impact = null
      this.errors.impact = ''
      this.loading.impact = true
      this.impactTimer = setTimeout(() => { if (!this.destroyed) this.loadImpact() }, 500)
    },
    async loadImpact(policy = this.form.policy) {
      clearTimeout(this.impactTimer)
      const snapshot = clone(policy)
      snapshot.min_version = nullable(snapshot.min_version)
      snapshot.blocked_versions = [...new Set(snapshot.blocked_versions.map(nullable).filter(Boolean))]
      const sequence = ++this.impactSequence
      this.impact = null
      this.errors.impact = ''
      this.loading.impact = true
      if ((snapshot.min_version && !parseVersion(snapshot.min_version)) || snapshot.blocked_versions.some(version => !parseVersion(version))) {
        this.errors.impact = '请先填写有效版本，再查看影响预估'
        this.loading.impact = false
        return false
      }
      try {
        const result = await getLauncherUpdateImpact(snapshot)
        if (this.destroyed || sequence !== this.impactSequence || impactKey(snapshot) !== impactKey(this.form.policy)) return false
        this.impact = result
        return true
      } catch (error) {
        if (sequence === this.impactSequence) this.errors.impact = launcherUpdateErrorMessage(error, '读取影响预估失败')
        return false
      } finally { if (sequence === this.impactSequence) this.loading.impact = false }
    },
    buildConfigPayload() {
      const sources = clone(this.form.sources)
      sources.github.owner = nullable(sources.github.owner)
      sources.github.repo = nullable(sources.github.repo)
      sources.r2.url = nullable(sources.r2.url)
      sources.fallback.url = nullable(sources.fallback.url)
      sources.download_page_url = nullable(sources.download_page_url)
      const policy = clone(this.form.policy)
      policy.min_version = nullable(policy.min_version)
      policy.blocked_versions = [...new Set(policy.blocked_versions.map(nullable).filter(Boolean))]
      policy.running_grace_seconds = 300
      if (this.effectiveChoice === 'custom' && this.effectiveEdited) policy.effective_at = beijingIso(this.effectiveInput)
      if (this.effectiveChoice === 'immediate' && (this.effectiveEdited || (policy.mode === 'force' && !policy.effective_at))) policy.effective_at = new Date().toISOString()
      return { expected_revision: this.configMeta.revision, sources, policy }
    },
    validateConfig(payload) {
      const errors = {}
      const github = payload.sources.github
      if (github.owner && !/^[A-Za-z0-9-]{1,39}$/.test(github.owner)) errors.owner = 'Owner 只能包含字母、数字和短横线'
      if (github.repo && !/^[A-Za-z0-9._-]{1,100}$/.test(github.repo)) errors.repo = 'Repo 只能包含字母、数字、点、下划线和短横线'
      if (!!github.owner !== !!github.repo) errors.owner = errors.repo = 'Owner 和 Repo 必须同时填写或同时留空'
      if (!validHttps(payload.sources.r2.url)) errors.r2 = '请输入不含账号密码的 HTTPS 地址，最多 512 字符'
      if (!validHttps(payload.sources.fallback.url)) errors.fallback = '请输入不含账号密码的 HTTPS 地址，最多 512 字符'
      if (!validHttps(payload.sources.download_page_url)) errors.download_page_url = '请输入不含账号密码的 HTTPS 地址，最多 512 字符'
      if (payload.policy.min_version && !parseVersion(payload.policy.min_version)) errors.min_version = '请输入有效版本，如 3.0.0 或 3.0.0-dev'
      if (payload.policy.blocked_versions.length > 20 || payload.policy.blocked_versions.some(version => !parseVersion(version))) errors.blocked_versions = '最多 20 个版本，均须符合版本号格式'
      if (this.effectiveChoice === 'custom' && !beijingIso(this.effectiveInput)) errors.effective_at = '请选择有效的北京时间'
      if (payload.policy.effective_at && !Number.isFinite(Date.parse(payload.policy.effective_at))) errors.effective_at = '生效时间无效'
      if (textLength(payload.policy.message) > 200) errors.message = '提示文案不能超过 200 字'
      this.configFieldErrors = errors
      if (Object.keys(errors).length) this.$message.warning('请检查更新策略及线路配置中的错误')
      return !Object.keys(errors).length
    },
    async prepareSave() {
      if (!this.configReady || this.configBusy || this.confirmVisible || this.revisionConflict) return
      const payload = this.buildConfigPayload()
      if (!this.validateConfig(payload)) return
      this.errors.config = ''
      const reasons = confirmationReasons(this.originalConfig.policy, payload.policy)
      if (reasons.length) {
        this.pendingPayload = payload
        this.confirmReasons = reasons
        this.confirmText = ''
        this.confirmVisible = true
        await this.loadImpact(payload.policy)
      } else { await this.submitConfig(payload) }
    },
    clearConfirmation() {
      if (this.saving) return
      this.pendingPayload = null
      this.confirmText = ''
      this.confirmReasons = []
    },
    async confirmSave() {
      if (!this.pendingPayload || this.saving || this.confirmText !== '强制更新' || !this.impact || this.loading.impact || this.errors.impact || this.revisionConflict) return
      await this.submitConfig({ ...clone(this.pendingPayload), confirm_text: this.confirmText })
    },
    async submitConfig(payload) {
      if (this.saving) return
      this.saving = true
      const draftNeeded = payload.policy.mode === 'force' && this.effectiveChoice !== 'immediate' && Date.parse(payload.policy.effective_at) > Date.now()
      let saved = null
      try {
        saved = await saveLauncherUpdateConfig(payload)
        this.applyConfig(saved)
        this.errors.config = ''
        this.confirmVisible = false
        this.$message.success('线路配置与更新策略已保存')
        this.loadStatus()
      } catch (error) {
        this.errors.config = launcherUpdateErrorMessage(error, '保存配置失败')
        if (error.code === 'LAUNCHER_UPDATE_REVISION_CONFLICT') {
          this.revisionConflict = true
          this.confirmVisible = false
        }
        if (error.code === 'LAUNCHER_UPDATE_CONFIRM_REQUIRED') {
          this.pendingPayload = clone(payload)
          this.confirmReasons = ['平台要求确认此策略变更']
          this.confirmText = ''
          this.confirmVisible = true
          await this.loadImpact(payload.policy)
        }
      } finally { this.saving = false }
      if (saved && draftNeeded && !this.destroyed) {
        try {
          await this.$confirm('强制更新策略已保存，是否生成提前通知用户的公告草稿？', '生成更新公告', { type: 'info', confirmButtonText: '生成草稿', cancelButtonText: '暂不生成' })
          this.openForceAnnouncement(saved.policy)
        } catch (error) { /* 取消生成不影响已保存的策略。 */ }
      }
    },
    openAudits() { this.auditPage = 1; this.auditsVisible = true; this.loadAudits() },
    changeAuditPage(page) { this.auditPage = page; this.loadAudits() },
    async loadAudits() {
      const sequence = ++this.auditsSequence
      this.loading.audits = true
      this.errors.audits = ''
      try {
        const data = await listLauncherUpdateAudits({ page: this.auditPage, size: 20 })
        if (sequence === this.auditsSequence) this.audits = data
      } catch (error) {
        if (sequence === this.auditsSequence) this.errors.audits = launcherUpdateErrorMessage(error, '读取修改记录失败')
      } finally { if (sequence === this.auditsSequence) this.loading.audits = false }
    },
    changeAnnouncementPage(page) { this.announcementPage = page; this.loadAnnouncements() },
    async loadAnnouncements() {
      const sequence = ++this.announcementsSequence
      this.loading.announcements = true
      this.errors.announcements = ''
      try {
        const data = await listLauncherAnnouncements({ page: this.announcementPage, size: 20 })
        if (sequence === this.announcementsSequence) this.announcements = data
      } catch (error) {
        if (sequence === this.announcementsSequence) this.errors.announcements = launcherUpdateErrorMessage(error, '读取公告列表失败')
      } finally { if (sequence === this.announcementsSequence) this.loading.announcements = false }
    },
    openAnnouncement(row) {
      if (this.announcementSaving) return
      const now = new Date().toISOString()
      this.announcementOriginalTimes = row ? { starts_at: row.starts_at, ends_at: row.ends_at } : { starts_at: now, ends_at: null }
      this.announcementForm = row ? {
        id: row.id, kind: row.kind, title: row.title, content: row.content,
        target_below_version: row.target_below_version,
        starts_at: beijingTime(row.starts_at), ends_at: beijingTime(row.ends_at), display: [...row.display]
      } : { ...emptyAnnouncement(), starts_at: beijingTime(now) }
      this.announcementTarget = row && row.target_below_version ? 'below' : 'all'
      this.announcementError = ''
      this.announcementFieldErrors = {}
      this.announcementVisible = true
    },
    openForceAnnouncement(policy) {
      this.openAnnouncement()
      const effective = beijingTime(policy.effective_at)
      this.announcementForm.kind = 'force'
      this.announcementForm.title = `启动器将于 ${effective} 强制更新`
      this.announcementForm.content = `启动器将于北京时间 ${effective} 强制更新，最低支持版本为 ${policy.min_version || '未设置'}。请提前更新，避免影响使用。`
      this.announcementTarget = policy.min_version ? 'below' : 'all'
      this.announcementForm.target_below_version = policy.min_version
      this.announcementForm.ends_at = effective
      this.announcementOriginalTimes.ends_at = policy.effective_at
    },
    buildAnnouncementPayload() {
      const form = this.announcementForm
      const original = this.announcementOriginalTimes
      const timeValue = field => original[field] && form[field] === beijingTime(original[field]) ? original[field] : beijingIso(form[field])
      return {
        kind: form.kind, title: form.title.trim(), content: form.content.trim(),
        target_below_version: this.announcementTarget === 'below' ? nullable(form.target_below_version) : null,
        starts_at: timeValue('starts_at'), ends_at: timeValue('ends_at'),
        display: [...form.display]
      }
    },
    validateAnnouncement(payload) {
      const errors = {}
      if (!payload.title || textLength(payload.title) > 40) errors.title = '标题不能为空，最多 40 字'
      if (!payload.content || textLength(payload.content) > 500) errors.content = '内容不能为空，最多 500 字'
      if (this.announcementTarget === 'below' && !parseVersion(payload.target_below_version)) errors.target_below_version = '请选择或输入有效版本'
      if (!payload.starts_at) errors.starts_at = '请选择有效的开始时间（北京时间）'
      if (!payload.ends_at || (payload.starts_at && Date.parse(payload.ends_at) <= Date.parse(payload.starts_at))) errors.ends_at = '结束时间必须晚于开始时间'
      if (!payload.display.length) errors.display = '至少选择一种展示方式'
      this.announcementFieldErrors = errors
      return !Object.keys(errors).length
    },
    async saveAnnouncement() {
      if (this.announcementSaving) return
      const payload = this.buildAnnouncementPayload()
      if (!this.validateAnnouncement(payload)) return
      this.announcementSaving = true
      this.announcementError = ''
      try {
        if (this.announcementForm.id) await updateLauncherAnnouncement(this.announcementForm.id, payload)
        else await createLauncherAnnouncement(payload)
        this.$message.success('公告已保存')
        this.announcementVisible = false
        this.announcementPage = 1
        await this.loadAnnouncements()
      } catch (error) { this.announcementError = launcherUpdateErrorMessage(error, '保存公告失败') }
      finally { this.announcementSaving = false }
    },
    async removeAnnouncement(row) {
      if (this.deletingId !== null || this.announcementSaving) return
      this.deletingId = row.id
      try {
        try { await this.$confirm(`确定删除公告“${row.title}”？删除后启动器将不再展示，修改记录会保留。`, '删除公告', { type: 'warning', confirmButtonText: '确认删除', cancelButtonText: '取消' }) }
        catch (error) { return }
        await deleteLauncherAnnouncement(row.id)
        this.$message.success('公告已删除')
        if (this.announcements.items.length === 1 && this.announcementPage > 1) this.announcementPage -= 1
        await this.loadAnnouncements()
      } catch (error) { this.errors.announcements = launcherUpdateErrorMessage(error, '删除公告失败') }
      finally { this.deletingId = null }
    }
  }
}
</script>

<style scoped>
.ignore-launcher-update { box-sizing: border-box; padding: 16px 20px 80px; height: calc(100vh - 60px); overflow: auto; color: #303133; background: #f5f7fa; }
.ignore-launcher-update .page-header, .ignore-launcher-update .section-heading, .ignore-launcher-update .config-footer { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.ignore-launcher-update .page-header { margin-bottom: 16px; min-height: 34px; flex-wrap: wrap; }
.ignore-launcher-update .page-header >>> .el-breadcrumb { line-height: 24px; font-size: 16px; }
.ignore-launcher-update .section-card { margin-bottom: 16px; border-radius: 4px; }
.ignore-launcher-update .section-card >>> .el-card__header { padding: 12px 16px; }
.ignore-launcher-update .section-card >>> .el-card__body { padding: 16px; }
.ignore-launcher-update .section-heading { font-size: 15px; font-weight: 600; min-height: 28px; flex-wrap: wrap; }
.ignore-launcher-update .section-alert { margin-bottom: 16px; }
.ignore-launcher-update .overview-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 20px; }
.ignore-launcher-update .overview-grid strong { display: block; margin-top: 8px; font-size: 18px; overflow-wrap: anywhere; }
.ignore-launcher-update .muted, .ignore-launcher-update .cell-note, .ignore-launcher-update .form-note { color: #909399; font-size: 12px; line-height: 20px; }
.ignore-launcher-update .cell-note { margin-top: 4px; }
.ignore-launcher-update .plain-text, .ignore-launcher-update .break-text { white-space: pre-wrap; overflow-wrap: anywhere; word-break: break-word; }
.ignore-launcher-update .plain-text { line-height: 1.7; max-height: 420px; overflow: auto; }
.ignore-launcher-update .error-text, .ignore-launcher-update .danger-action { color: #f56c6c; }
.ignore-launcher-update .error-text { font-size: 12px; margin-top: 6px; }
.ignore-launcher-update .error-row { display: flex; align-items: center; gap: 10px; margin-bottom: 16px; }
.ignore-launcher-update .error-row .el-alert { flex: 1; min-width: 0; }
.ignore-launcher-update .after-table { margin-top: 16px; margin-bottom: 0; }
.ignore-launcher-update .release-title { display: inline-block; min-width: 135px; margin-right: 16px; }
.ignore-launcher-update .summary-area { min-height: 48px; }
.ignore-launcher-update .summary-line { margin-bottom: 16px; line-height: 24px; }
.ignore-launcher-update .summary-line .muted { display: block; }
.ignore-launcher-update .version-bar { display: grid; grid-template-columns: 160px minmax(120px, 1fr) 90px; align-items: center; gap: 16px; margin: 12px 0; }
.ignore-launcher-update .version-name { font-size: 13px; overflow-wrap: anywhere; }
.ignore-launcher-update .version-count { text-align: right; font-size: 13px; }
.ignore-launcher-update .filter-form { display: flex; align-items: flex-start; flex-wrap: wrap; gap: 0 12px; }
.ignore-launcher-update .filter-form .el-form-item { margin-right: 0; margin-bottom: 12px; }
.ignore-launcher-update .filter-form .el-input, .ignore-launcher-update .filter-form .el-select { width: 185px; }
.ignore-launcher-update .pagination { margin-top: 16px; text-align: right; overflow-x: auto; }
.ignore-launcher-update .config-form { max-width: 760px; }
.ignore-launcher-update .full-width, .ignore-launcher-update .source-form .el-input { width: 100%; }
.ignore-launcher-update .date-input { margin-top: 10px; }
.ignore-launcher-update .switch-note { margin-left: 12px; color: #606266; font-size: 12px; }
.ignore-launcher-update .config-form >>> .el-form-item__label { line-height: 20px; padding-top: 6px; }
.ignore-launcher-update .impact-area { min-height: 42px; }
.ignore-launcher-update .source-form { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); column-gap: 32px; }
.ignore-launcher-update .source-fields { min-width: 0; }
.ignore-launcher-update .source-form .el-form-item { margin-bottom: 18px; }
.ignore-launcher-update .config-footer { border-top: 1px solid #ebeef5; padding-top: 16px; align-items: flex-end; flex-wrap: wrap; }
.ignore-launcher-update .footer-actions { display: flex; gap: 8px; flex-wrap: wrap; }
.ignore-launcher-update .footer-actions .el-button { margin: 0; }
.ignore-launcher-update .confirmation-impact { min-height: 100px; }
.ignore-launcher-update .audit-snapshots { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 20px; }
.ignore-launcher-update .audit-snapshots pre { background: #f5f7fa; padding: 12px; border-radius: 4px; white-space: pre-wrap; overflow-wrap: anywhere; max-height: 340px; overflow: auto; font: 12px/1.6 monospace; }
.ignore-launcher-update .announcement-editor { display: grid; grid-template-columns: minmax(0, 1.2fr) minmax(0, 1fr); gap: 28px; }
.ignore-launcher-update .announcement-editor .el-date-editor.el-input { width: 100%; }
.ignore-launcher-update .announcement-editor .el-form, .ignore-launcher-update .announcement-preview { min-width: 0; }
.ignore-launcher-update .announcement-preview h4 { margin: 0 0 16px; font-size: 14px; overflow-wrap: anywhere; }
.ignore-launcher-update .preview-banner { display: flex; gap: 10px; padding: 12px; border-left: 3px solid #e6a23c; background: #fdf6ec; color: #8a5b17; margin-bottom: 20px; font-size: 13px; }
.ignore-launcher-update .preview-banner > div { min-width: 0; overflow-wrap: anywhere; }
.ignore-launcher-update .preview-banner .el-icon-bell { margin-top: 4px; }
.ignore-launcher-update .preview-window { border: 1px solid #dcdfe6; border-radius: 4px; background: #fff; }
.ignore-launcher-update .preview-window-title { padding: 10px 14px; font-size: 12px; border-bottom: 1px solid #ebeef5; color: #909399; }
.ignore-launcher-update .preview-window-body { padding: 16px; font-size: 13px; }
.ignore-launcher-update .preview-window-footer { padding: 0 16px 16px; text-align: right; }
.ignore-launcher-update >>> .el-dialog { max-width: calc(100vw - 32px); border-radius: 4px; }
.ignore-launcher-update >>> .el-dialog__body { max-height: 72vh; overflow: auto; }
@media (max-width: 900px) {
  .ignore-launcher-update .overview-grid, .ignore-launcher-update .source-form, .ignore-launcher-update .announcement-editor, .ignore-launcher-update .audit-snapshots { grid-template-columns: 1fr; }
  .ignore-launcher-update .overview-grid { gap: 16px; }
  .ignore-launcher-update .version-bar { grid-template-columns: 105px minmax(85px, 1fr) 65px; gap: 8px; }
  .ignore-launcher-update { padding: 12px 12px 80px; }
}
@media (max-width: 540px) {
  .ignore-launcher-update .config-form >>> .el-form-item__label, .ignore-launcher-update .source-form >>> .el-form-item__label, .ignore-launcher-update .announcement-editor >>> .el-form-item__label { float: none; display: block; width: auto !important; text-align: left; padding: 0 0 6px; }
  .ignore-launcher-update .config-form >>> .el-form-item__content, .ignore-launcher-update .source-form >>> .el-form-item__content, .ignore-launcher-update .announcement-editor >>> .el-form-item__content { margin-left: 0 !important; }
  .ignore-launcher-update .filter-form { display: block; }
  .ignore-launcher-update .filter-form .el-input, .ignore-launcher-update .filter-form .el-select { width: 100%; }
  .ignore-launcher-update .filter-form .el-form-item { display: block; }
  .ignore-launcher-update .filter-form >>> .el-form-item__content { display: block; }
  .ignore-launcher-update .error-row { flex-wrap: wrap; }
}
</style>
